import { observable, autorun, makeObservable } from 'mobx'
import * as R from 'ramda'
import { uniqBy } from 'lodash'
import i18n from 'i18next'

import Venue from '../../Models/Venue'

class VenueStore {
    @observable filteredList = []

    filters = {
        city: { label: 'View all', value: 'all' },
        country: { label: 'View all', value: '' },
        status: {
            active: { name: 'active', label: i18n.t('table.col.status.active'), value: true },
            inactive: { name: 'inactive', label: i18n.t('table.col.status.inactive'), value: false },
        },
    }

    search = {
        venues: [],
        cities: [],
        countries: [],
        countryMap: {},
    }

    edit = {
        venues: [],
        venue: {},
        countries: [],
        surveyCount: 0,
    }

    @observable searchLoading = true

    @observable formFields = []

    @observable formLoading = true

    constructor(store) {
        makeObservable(this)
        this.appStore = store

        autorun(() => {
            if (this.formFields.length > 0) this.formLoading = false
        })
    }

    initSearch = async () => {
        this.searchLoading = true
        try {
            const venues = await this.getVenues()
            this.search.venues = venues
            this.filteredList = this.search.venues

            const cities = this.getCities(venues)
            this.search.cities = [{ label: 'View all', value: 'all' }, ...cities]

            const countries = await this.getCountries()
            this.search.countries = [{ label: 'View all', value: '' }, ...countries]

            this.search.countryMap = this.search.countries.reduce((m, { value, label }) => (
                Object.assign(m, {
                    [value]: label,
                })
            ), {})

            this.search()
        } catch (error) {
            console.log('VenueStore initSearch error ', error)
        }
        this.searchLoading = false
    }

    initEdit = async (venueId, handleCloseModal) => {
        this.formLoading = true
        try {
            this.edit.countries = await this.getCountries()
            this.edit.surveyCount = await this.getSurveyCount(venueId)

            const venues = await this.getVenues()
            this.edit.venues = this.formatVenues(venues, venueId)

            if (venueId !== 0) {
                this.edit.venue = await this.getVenue(venueId)
            } else this.edit.venue = new Venue({})

            this.defineFormFields()
        } catch (error) {
            console.log('VenueStore initEdit error ', error)
            handleCloseModal()
            // this.appStore.addNotification(null, 'Failed to connect with server', 'error')
        }
    }

    setField = ({ value, name }) => {
        const { venue } = this.edit
        if (name.startsWith('status-')) {
            const status = name.split('-')[1]
            if (status === 'active') venue.active = value
            else venue.active = !value
            this.defineFormFields()
        } else venue[name] = value
    }

    defineFormFields = () => {
        const { venue, countries } = this.edit

        this.formFields = [
            {
                type: 'input',
                name: 'name',
                label: i18n.t('venue.form.name', 'Name'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: venue.name,
                isMandatory: true,
                errorMessage: i18n.t('venue.form.error.name'),
            },
            {
                type: 'input',
                name: 'city',
                label: i18n.t('city', 'Town / city'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: venue.city,
                isMandatory: true,
                errorMessage: i18n.t('venue.form.error.city'),
            },
            {
                type: 'select',
                name: 'country',
                label: i18n.t('country', 'Country'),
                placeholder: i18n.t('form.select.placeholder', 'Select here'),
                defaultValue: venue.country,
                options: countries,
                isMandatory: true,
                errorMessage: i18n.t('venue.form.error.country'),
            },
            {
                type: 'checkbox-reactive',
                cssClass: 'horizontal',
                name: 'venueStatus',
                label: i18n.t('venue.form.venueStatus'),
                fields: [
                    {
                        type: 'checkbox',
                        label: i18n.t('active'),
                        name: 'status-active',
                        checked: venue.active,
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('inactive'),
                        name: 'status-inactive',
                        checked: !venue.active,
                    },
                ],
            },
        ]
    }

    resetFormState = () => {
        this.formFields = []
    }

    getSurveyCount = async (venueId) => {
        let surveyCount = 0
        try {
            surveyCount = await this.appStore.http.get(`/venue/v2/${venueId}/surveys`)
        } catch (e) {
            console.error(e)
        }

        return surveyCount
    }

    getVenues = () => this.appStore.http.get('/venue/v2')

    formatVenues = (venues, id) => (
        venues
            .filter((v) => v.venueId !== parseInt(id, 10))
            .map(({ name, venueId }) => ({
                value: venueId,
                label: name,
            }))
    )

    getCities = (venues) => {
        const cities = uniqBy(
            venues.reduce((m, { city }) => (
                m.concat({
                    value: city,
                    label: city,
                })
            ), []),
            'value',
        )
        return this.sortAsc(cities)
    }

    getVenue = async (venueId) => {
        const data = await this.appStore.http.get(`/venue/v2/${venueId}`)

        return new Venue(data)
    }

    sortAsc = (values) => (
        R.sortWith([
            R.ascend(
                R.compose(
                    R.toLower,
                    R.prop('label'),
                ),
            ),
        ], values)
    )

    getCountries = async () => {
        const result = await this.appStore.http.get('/refcode/country')
        const countries = result.response.map(({ description, name }) => ({
            value: name,
            label: description,
        }))

        return this.sortAsc(countries)
    }

    changeCity = (obj) => {
        this.filters.city = R.isNil(obj)
            ? null
            : obj
        this.search()
    }

    changeCountry = (obj) => {
        this.filters.country = R.isNil(obj)
            ? null
            : obj
        this.search()
    }

    toggleStatus = (name) => {
        this.filters.status[name].value = !this.filters.status[name].value
        this.search()
    }

    search = () => {
        const { filters } = this
        const { city, country, status } = filters
        const searchStr = R.pathOr('', ['searchStr'], this.searchModule)

        const filtered = this.search.venues
            .filter((venue) => (
                venue.active ? status.active.value : status.inactive.value
            ))
            .filter((venue) => (
                city.value === 'all' || venue.city === city.value
            ))
            .filter((venue) => (
                country.value === '' || venue.country === country.value
            ))

        if (searchStr === '') {
            this.filteredList = filtered
        } else if (!Number.isNaN(parseInt(searchStr, 10))) {
            this.filteredList = filtered
                .filter((p) => p.venueId === parseInt(searchStr, 10))
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = filtered
                .filter((p) => (
                    p.name.match(re)
                        || p.city.match(re)
                ))
        }
    }

    update = async (obj) => {
        const response = await this.appStore.http.put(`/venue/v2/${obj.venueId}`, JSON.stringify(obj))
        return response
    }

    save = async (obj) => {
        const response = await this.appStore.http.post('/venue/v2', JSON.stringify(obj))
        return response
    }

    deleteVenue = async (venueId, newVenueId) => {
        const response = await this.appStore.http.delete(`/venue/v2/${venueId}?newVenueId=${newVenueId}`)
        return response
    }

    resetFilter = () => {
        this.filters = {
            city: { label: 'View all', value: 'all' },
            country: { label: 'View all', value: '' },
            status: {
                active: { name: 'active', label: i18n.t('table.col.status.active'), value: true },
                inactive: { name: 'inactive', label: i18n.t('table.col.status.inactive'), value: false },
            },
        }
        this.search()
    }
}

export default VenueStore
